import React from "react";
import { ArrowRight, Check, CircleDashed, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { num, ago, TYPE_LABEL, FAIL_LABEL } from "@/lib/sk";
import { StatusPill, CodeLine } from "./Proposal";
import { APPLICATION_LABEL, FEEDBACK_LABEL, cellText } from "./model";

const stop = (fn) => (e) => { e.stopPropagation(); e.preventDefault(); fn(); };
const link = "max-w-full truncate text-left outline-none hover:underline focus-visible:underline";
export const TONE = { ok: "text-[hsl(var(--brand-strong))]", warn: "text-[hsl(var(--v-assumed))]", eng: "text-[hsl(var(--v-eng))]", blocked: "text-[hsl(var(--v-blocked))]" };
const TONE_BG = { ok: "bg-[hsl(var(--brand-soft))]", warn: "bg-[hsl(var(--v-assumed-bg))]", eng: "bg-[hsl(var(--v-eng-bg))]", blocked: "bg-[hsl(var(--v-blocked-bg))]" };

/** Which way a read-only cell opens the valve: the tag → the panel, the actuator → the configurator, status/next step → the assessment. */
export function intentOf(col, v) {
  const ns = v?.proposal?.next_step;
  if (col.kind === "actuator" || col.kind === "code") return "configure";
  if (col.kind === "status" || col.kind === "pill" || col.kind === "checks" || col.key === "why") return "assess";
  if (col.kind === "next") return ns?.key === "choose" || ns?.key === "change" ? "configure" : ns?.key === "done" ? "view" : "assess";
  return "view";
}

export function ValveCell({ v, onOpen, compact }) {
  return (
    <div className="min-w-0">
      <button type="button" onClick={stop(() => onOpen(v, "view"))} className={cn(link, "block font-mono text-[13.5px] font-semibold tracking-[-0.01em] text-foreground")} title={`Åpne ${v.tag}`} data-testid={`ventil-open-${v.tag}`}>{v.tag}</button>
      {!compact ? <div className="k-meta truncate">{[TYPE_LABEL[v.type], v.dn ? `DN ${v.dn}` : null, v.valve_model].filter(Boolean).join(" · ") || <span className="text-muted-foreground/50">type og DN mangler</span>}</div> : null}
    </div>
  );
}

export function FunctionCell({ v }) {
  const fb = v.feedback ? FEEDBACK_LABEL[v.feedback] : null;
  return (
    <div className="min-w-0">
      <div className="truncate">{APPLICATION_LABEL[v.application] || <span className="text-muted-foreground/50">–</span>}{v.fail_mode ? <span className="text-foreground/70"> · {FAIL_LABEL[v.fail_mode]}</span> : null}</div>
      <div className="k-meta truncate">{[fb ? `ind. ${fb.toLowerCase()}` : null, v.esd_group ? `ESD ${v.esd_group}` : null, v.closing_time_s ? `lukk ${num(v.closing_time_s)} s` : null].filter(Boolean).join(" · ")}</div>
    </div>
  );
}

/** Who stands behind the actuator in the row: the system, or a named person – a different question from whether it is approved. */
export function originOf(v) {
  const p = v.proposal || {};
  const sel = v.selection;
  if (sel) return { text: `Valgt av ${sel.by}${sel.from_tag ? ` · fra ${sel.from_tag}` : ""}`, title: `${ago(sel.at)}${sel.reason ? ` · ${sel.reason}` : ""} · systemets forslag: ${p.system?.code || "ingen"}`, own: true };
  if (p.code) return { text: "Foreslått av systemet", own: false };
  return { text: p.status === "blokkert" ? "Ingen aktuator – data mangler" : p.status === "krever_avklaring" && p.wp_bar == null ? "Venter på prosjekttrykk" : "Ingen aktuator", own: false };
}

export function ActuatorCell({ v, onOpen, compact }) {
  const p = v.proposal || {};
  const o = originOf(v);
  const open = stop(() => onOpen(v, "configure"));
  return (
    <div className="group/act min-w-0">
      <div className="flex min-w-0 items-center gap-2">
        <button type="button" onClick={open} className={cn(link, "block")} title={p.code ? "Åpne konfiguratoren" : "Velg aktuator"} data-testid={`ventil-configure-${v.tag}`}>
          {p.code ? <CodeLine code={p.code} baseCode={p.base_code} className="text-[13px]" /> : <span className="text-[hsl(var(--brand-strong))]">{p.wp_bar == null ? "–" : "Velg aktuator"}</span>}
        </button>
        {p.code && !compact ? <span className="ml-auto hidden shrink-0 text-[12px] text-[hsl(var(--brand-strong))] group-hover/act:inline" aria-hidden>Endre</span> : null}
        {compact && p.selected ? <span className="shrink-0 rounded-full bg-[hsl(var(--brand-soft))] px-1.5 text-[10.5px] font-medium text-[hsl(var(--brand-strong))]" title={o.title} data-testid={`ventil-selected-${v.tag}`}>valgt</span> : null}
      </div>
      {!compact ? <div className={cn("k-meta truncate", o.own && "text-[hsl(var(--brand-strong))]")} title={o.title} data-testid={`ventil-origin-${v.tag}`}>{o.text}</div> : null}
    </div>
  );
}

export function StatusCell({ v, onOpen, compact }) {
  const p = v.proposal || {};
  const ns = p.next_step;
  if (!p.status) return null;
  return (
    <button type="button" onClick={stop(() => onOpen(v, "assess"))} className="block min-w-0 max-w-full text-left outline-none" title={ns?.why || undefined} data-testid={`ventil-status-${v.tag}`}>
      <StatusPill status={p.status} short={compact} />
      {!compact && ns?.why_label && ns.key !== "done" ? <div className={cn("k-meta mt-0.5 truncate", ns.tone === "blocked" && "text-[hsl(var(--v-blocked))]")}>{ns.why_label}</div> : null}
      {!compact && ns?.key === "done" ? <div className="k-meta mt-0.5 truncate">{ns.why}</div> : null}
    </button>
  );
}

export function NextCell({ v, onOpen }) {
  const ns = v.proposal?.next_step;
  if (!ns) return null;
  if (ns.key === "done") return <span className="k-meta" data-testid={`ventil-next-${v.tag}`} data-key="done">–</span>;
  return (
    <button type="button" onClick={stop(() => onOpen(v, intentOf({ kind: "next" }, v)))} title={ns.why || undefined} className={cn("group/next inline-flex h-7 max-w-full items-center gap-1.5 rounded-full px-2.5 text-[12.5px] font-medium outline-none transition-colors hover:brightness-95 focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand)/0.3)]", TONE[ns.tone] || TONE.warn, TONE_BG[ns.tone] || TONE_BG.warn)} data-testid={`ventil-next-${v.tag}`} data-key={ns.key}>
      <span className="truncate">{ns.label}</span><ArrowRight className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover/next:opacity-100" />
    </button>
  );
}

const MARK = { true: [Check, "text-[hsl(var(--v-ok))] bg-[hsl(var(--v-ok-bg))]"], false: [X, "text-[hsl(var(--v-blocked))] bg-[hsl(var(--v-blocked-bg))]"], null: [CircleDashed, "text-muted-foreground bg-black/[0.05]"] };
export function ChecksCell({ v }) {
  const checks = v.proposal?.checks || [];
  if (!checks.length) return <span className="text-muted-foreground/40">·</span>;
  return <span className="inline-flex items-center gap-1" data-testid={`ventil-checks-${v.tag}`}>{checks.map((c) => { const [Icon, cls] = MARK[String(c.ok)]; return <span key={c.key} className={cn("flex h-[16px] w-[16px] items-center justify-center rounded-full", cls)} title={`${c.label}: ${c.detail}`} data-ok={String(c.ok)}><Icon className="h-[9px] w-[9px]" strokeWidth={3} /></span>; })}</span>;
}

export function AdapterCell({ v }) {
  const fl = v.proposal?.flange;
  if (!fl) return <span className="text-muted-foreground/40">·</span>;
  if (fl.needed === false) return <span className="text-muted-foreground">direkte</span>;
  if (fl.line?.name) return <span className="truncate">{fl.line.name}</span>;
  return <span className={cn(fl.ok === false ? "text-[hsl(var(--v-assumed))]" : "text-muted-foreground")} title={fl.detail}>{fl.ok === false ? "uavklart" : fl.needed ? "trengs" : "–"}</span>;
}

/** A plain data cell: the normalised value in words, «?» when the configurator asks about it, «·» when empty. */
export function DataCell({ v, col, issue }) {
  if (col.get) { const x = col.get(v); return x == null || x === "" ? <span className="text-muted-foreground/40">–</span> : <span className={cn(col.bad?.(v) && "font-medium text-[hsl(var(--v-blocked))]")}>{typeof x === "number" ? num(x, col.digits ?? (Number.isInteger(x) ? 0 : 1)) : String(x)}</span>; }
  const t = cellText(col.key, v[col.key]);
  if (t) return t;
  return issue ? <span className="text-[hsl(var(--v-assumed))]">?</span> : <span className="text-muted-foreground/35">·</span>;
}
